'use client';

import { useRouter } from 'next/navigation';
import { useUiStore } from '@/stores/ui-store';
import { Breadcrumbs } from '@/components/catalog/Breadcrumbs';
import type { LibraryConnection } from '@/lib/opds/types';

export function Header({
  title,
  actions,
  showBack = false,
  currentAbsoluteUrl,
  library,
  includeCurrent = false,
}: {
  title?: string;
  actions?: React.ReactNode;
  showBack?: boolean;
  /** When provided alongside `library`, ancestor crumbs render inline before the title. */
  currentAbsoluteUrl?: string;
  library?: LibraryConnection;
  /** Forwarded to Breadcrumbs — set on book detail pages where the URL is the parent feed. */
  includeCurrent?: boolean;
}) {
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const router = useRouter();
  const showCrumbs = !!(currentAbsoluteUrl && library);

  return (
    <>
    <header className="fixed top-0 right-0 left-0 z-20 flex h-14 items-center gap-2 border-b border-neutral-200 bg-white px-4 lg:left-64">
      <button
        onClick={toggleSidebar}
        className="rounded-lg p-2 -ml-1 text-neutral-500 hover:bg-neutral-100 lg:hidden"
      >
        <svg className="h-6 w-6 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      {showBack && (
        <button
          onClick={() => router.back()}
          className="rounded-lg p-2 -ml-1 text-neutral-500 hover:bg-neutral-100"
        >
          <svg className="h-6 w-6 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      <nav
        aria-label={showCrumbs ? 'Breadcrumb' : undefined}
        className="flex min-w-0 flex-1 items-center gap-1.5 text-sm"
      >
        {showCrumbs && (
          <Breadcrumbs
            currentAbsoluteUrl={currentAbsoluteUrl!}
            library={library!}
            includeCurrent={includeCurrent}
          />
        )}
        {title && (
          <h1 className="min-w-0 truncate text-base font-semibold text-neutral-900 sm:text-lg">
            {title}
          </h1>
        )}
      </nav>

      <div className="ml-auto flex shrink-0 items-center gap-2">{actions}</div>
    </header>
    <div className="h-14" />
    </>
  );
}
